import { useCallback, useEffect, useMemo, useState } from "react";
import StaffFormModal from "@/pages/admin/users/components/StaffFormModal";
import PinModal from "@/pages/admin/users/components/PinModal";
import AdminModal from "@/pages/admin/users/components/AdminModal";
import { useBranches } from "@/hooks/useBranches";
import { supabase } from "@/lib/supabase";
import { callUsersApi } from "@/lib/usersApi";
import { useToast } from "@/context/ToastContext";
import type { StaffMember } from "@/types";

type TabKey = "all" | "admin" | "cashier";

const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "admin", label: "Administradores" },
  { key: "cashier", label: "Cajeros" },
];

export default function AdminUsersPage() {
  const toast = useToast();
  const { branches } = useBranches();

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("all");
  const [search, setSearch] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [pinMember, setPinMember] = useState<StaffMember | null>(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profilesRes, rolesRes, permRes, linkRes] = await Promise.all([
        supabase
          .from("profiles")
          .select(
            "id, full_name, last_name, role_id, branch_id, internal_email, phone, active, created_at",
          )
          .order("created_at", { ascending: true }),
        supabase.from("roles").select("id, name"),
        supabase.from("permissions").select("id, code"),
        supabase.from("user_permissions").select("profile_id, permission_id"),
      ]);

      if (profilesRes.error) {
        setError("No se pudieron cargar los usuarios.");
        setStaff([]);
        return;
      }

      const roleById = new Map<string, string>(
        (rolesRes.data ?? []).map((role: { id: string; name: string }) => [role.id, role.name]),
      );
      const codeById = new Map<string, string>(
        (permRes.data ?? []).map((perm: { id: string; code: string }) => [perm.id, perm.code]),
      );

      const permsByProfile = new Map<string, string[]>();
      (linkRes.data ?? []).forEach((link: { profile_id: string; permission_id: string }) => {
        const code = codeById.get(link.permission_id);
        if (!code) return;
        const current = permsByProfile.get(link.profile_id) ?? [];
        current.push(code);
        permsByProfile.set(link.profile_id, current);
      });

      const mapped = (profilesRes.data ?? []).map((prof) => ({
        ...prof,
        role_name: roleById.get(prof.role_id) ?? null,
        permission_codes: permsByProfile.get(prof.id) ?? [],
      })) as StaffMember[];

      setStaff(mapped);
    } catch {
      setError("No se pudieron cargar los usuarios.");
      setStaff([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const branchName = useCallback(
    (id: string | null) => branches.find((branch) => branch.id === id)?.name ?? "Sin sucursal",
    [branches],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return staff.filter((member) => {
      if (tab === "admin" && member.role_name !== "admin") return false;
      if (tab === "cashier" && member.role_name !== "cashier") return false;
      if (!term) return true;
      const full = `${member.full_name} ${member.last_name}`.toLowerCase();
      return full.includes(term);
    });
  }, [staff, tab, search]);

  const handleToggleActive = async (member: StaffMember) => {
    setTogglingId(member.id);
    const result = await callUsersApi({
      action: "update_cashier",
      id: member.id,
      full_name: member.full_name,
      last_name: member.last_name,
      branch_id: member.branch_id,
      active: !member.active,
      permissions: member.permission_codes,
    });
    setTogglingId(null);
    if (!result.ok) {
      toast.error(result.error ?? "No se pudo actualizar el estado.");
      return;
    }
    toast.success(member.active ? "Usuario desactivado." : "Usuario activado.");
    load();
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (member: StaffMember) => {
    setEditing(member);
    setFormOpen(true);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">
            Usuarios y permisos
          </h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Administra administradores, cajeros, sucursales asignadas y permisos de cada uno.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-user-add-line text-base"></i>
            Nuevo cajero
          </button>
          <button
            type="button"
            onClick={() => setAdminOpen(true)}
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100"
          >
            <i className="ri-shield-user-line text-base"></i>
            Nuevo administrador
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                tab === item.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="relative w-full lg:w-72">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nombre..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={load}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando usuarios...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-14 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-team-line text-2xl"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">Sin usuarios para mostrar</p>
          <p className="max-w-sm text-xs text-foreground-500">
            Crea un cajero para que pueda acceder al POS con su PIN de 6 dígitos.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((member) => {
            const isAdminRole = member.role_name === "admin";
            return (
              <div
                key={member.id}
                className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 lg:flex-row lg:items-center"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      isAdminRole
                        ? "bg-primary-500 text-background-50"
                        : "bg-secondary-200 text-secondary-900"
                    }`}
                  >
                    {(member.full_name?.[0] ?? "U").toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-semibold text-foreground-950">
                        {member.full_name} {member.last_name}
                      </p>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          isAdminRole
                            ? "bg-primary-100 text-primary-800"
                            : "bg-accent-100 text-accent-900"
                        }`}
                      >
                        {isAdminRole ? "Administrador" : "Cajero"}
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-foreground-500">
                      {member.internal_email
                        ? `Acceso por PIN · ${branchName(member.branch_id)}`
                        : branchName(member.branch_id)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 lg:w-56">
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                      <i className="ri-shield-keyhole-line text-base"></i>
                    </span>
                    {isAdminRole ? "Acceso total" : `${member.permission_codes.length} permisos`}
                  </div>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                      member.active
                        ? "bg-accent-100 text-accent-900"
                        : "bg-secondary-200 text-secondary-900"
                    }`}
                  >
                    {member.active ? "Activo" : "Inactivo"}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                  {!isAdminRole ? (
                    <button
                      type="button"
                      onClick={() => setPinMember(member)}
                      className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-semibold text-foreground-800 transition-colors hover:bg-background-200"
                    >
                      <i className="ri-lock-line text-sm"></i>
                      PIN
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => openEdit(member)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-semibold text-foreground-800 transition-colors hover:bg-background-200"
                  >
                    <i className="ri-edit-2-line text-sm"></i>
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleActive(member)}
                    disabled={togglingId === member.id}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-60 ${
                      member.active
                        ? "border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100"
                        : "border-accent-300 bg-accent-50 text-accent-900 hover:bg-accent-100"
                    }`}
                  >
                    {togglingId === member.id ? (
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                    ) : (
                      <i
                        className={`${member.active ? "ri-user-forbid-line" : "ri-user-follow-line"} text-sm`}
                      ></i>
                    )}
                    {member.active ? "Desactivar" : "Activar"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <StaffFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={load}
        branches={branches}
        editing={editing}
      />
      <PinModal
        open={Boolean(pinMember)}
        onClose={() => setPinMember(null)}
        onSaved={load}
        member={pinMember}
      />
      <AdminModal open={adminOpen} onClose={() => setAdminOpen(false)} onSaved={load} />
    </div>
  );
}